/**
 * Complimentary 10ml Modal System
 * Handles the 88ml + free 10ml offer modal for Ormonde Jayne
 *
 * When a customer adds an 88ml variant, this modal lets them choose
 * any complimentary 10ml fragrance. The discount is handled automatically
 * by Shopify's "Buy X Get Y" automatic discount.
 */

(function() {
  'use strict';

  const TravelCompendium = {
    // DOM References
    modal: null,

    // State
    currentProductId: null,
    currentProductTitle: null,
    currentVariantId: null,        // The 88ml variant being added
    current88mlVariantId: null,
    selectedTravelSet: null,
    isProcessing: false,

    // Configuration
    STORAGE_KEY: 'oj_tc_declined',

    /**
     * Initialize the system
     */
    init: function() {
      if (!window.travelCompendium || !window.travelCompendium.enabled) {
        return;
      }

      this.modal = document.getElementById('travel-compendium-modal');
      if (!this.modal) {
        return;
      }

      this.bindEvents();
    },

    /**
     * Bind modal event listeners
     */
    bindEvents: function() {
      // Close buttons and backdrop
      this.modal.querySelectorAll('[data-tc-close]').forEach(el => {
        el.addEventListener('click', (e) => {
          if (e.target.classList.contains('tc-modal-backdrop')) {
            const step2 = this.modal.querySelector('[data-tc-step="2"]');
            if (step2 && step2.style.display !== 'none') {
              return;
            }
          }
          this.close();
        });
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('open')) {
          this.close();
        }
      });

      // Step 1: Show selection
      const showSelectionBtn = this.modal.querySelector('[data-tc-action="show-selection"]');
      if (showSelectionBtn) {
        showSelectionBtn.addEventListener('click', () => this.showStep2());
      }

      // Step 1: Decline
      const declineBtn = this.modal.querySelector('[data-tc-action="decline"]');
      if (declineBtn) {
        declineBtn.addEventListener('click', () => this.decline());
      }

      // Step 2: Add both
      const addBothBtn = this.modal.querySelector('[data-tc-action="add-both"]');
      if (addBothBtn) {
        addBothBtn.addEventListener('click', () => this.addBothToCart());
      }

      // Step 2: Skip
      const skipBtn = this.modal.querySelector('[data-tc-action="skip"]');
      if (skipBtn) {
        skipBtn.addEventListener('click', () => this.decline());
      }
    },

    // Reference to the product form element that triggered the modal
    productFormElement: null,

    /**
     * Check if modal should be shown for this variant
     */
    shouldShowModal: function(productId, variantId) {
      const declined = this.getDeclinedProducts();
      if (declined.includes(String(productId))) {
        return false;
      }

      if (!window.travelCompendium || !window.travelCompendium.travelSets || window.travelCompendium.travelSets.length === 0) {
        return false;
      }

      return true;
    },

    /**
     * Get list of declined product IDs from session storage
     */
    getDeclinedProducts: function() {
      try {
        const stored = sessionStorage.getItem(this.STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        return [];
      }
    },

    /**
     * Mark a product as declined for this session
     */
    setDeclined: function(productId) {
      const declined = this.getDeclinedProducts();
      if (!declined.includes(String(productId))) {
        declined.push(String(productId));
        try {
          sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(declined));
        } catch (e) {
          // Silent fail for private browsing
        }
      }
    },

    /**
     * Open the modal
     */
    open: function() {
      // Reset to step 1
      const step1 = this.modal.querySelector('[data-tc-step="1"]');
      const step2 = this.modal.querySelector('[data-tc-step="2"]');
      if (step1) step1.style.display = 'block';
      if (step2) step2.style.display = 'none';

      this.selectedTravelSet = null;
      this.resetSelection();

      // Populate 10ml grid
      this.populateTravelSets();

      // Show modal
      this.modal.classList.add('open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Hide Gorgias chat button
      this.hideGorgiasChat();

      // Focus first button for accessibility
      setTimeout(() => {
        const firstButton = this.modal.querySelector('.tc-modal__btn--primary');
        if (firstButton) firstButton.focus();
      }, 100);

      // Track event
      this.trackEvent('tc_modal_seen', {
        product_id: this.currentProductId,
        product_title: this.currentProductTitle,
        variant_id: this.currentVariantId
      });
    },

    /**
     * Close the modal
     */
    close: function() {
      this.modal.classList.remove('open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      this.showGorgiasChat();
    },

    /**
     * Show step 2 (10ml fragrance selection)
     */
    showStep2: function() {
      const step1 = this.modal.querySelector('[data-tc-step="1"]');
      const step2 = this.modal.querySelector('[data-tc-step="2"]');

      if (step1) step1.style.display = 'none';
      if (step2) step2.style.display = 'block';

      // Preselect recommended if available
      this.preselectRecommended();

      this.trackEvent('tc_modal_accepted', {
        product_id: this.currentProductId,
        variant_id: this.currentVariantId
      });
    },

    /**
     * Populate the 10ml fragrances grid
     */
    populateTravelSets: function() {
      const grid = document.getElementById('tc-travel-sets-grid');
      if (!grid || !window.travelCompendium || !window.travelCompendium.travelSets) return;

      grid.innerHTML = '';

      window.travelCompendium.travelSets.forEach((set) => {
        const item = document.createElement('div');
        item.className = 'tc-modal__item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.setAttribute('tabindex', '0');
        item.dataset.variantId = set.variantId;
        item.dataset.productId = set.productId;
        item.dataset.title = set.productTitle;

        item.innerHTML = `
          <img src="${set.image}" alt="${set.productTitle}" class="tc-modal__item-image" loading="lazy">
          <h4 class="tc-modal__item-title">${set.productTitle}</h4>
          <p class="tc-modal__item-price">Complimentary</p>
        `;

        item.addEventListener('click', () => this.selectTravelSet(item, set));

        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectTravelSet(item, set);
          }
        });

        grid.appendChild(item);
      });
    },

    /**
     * Preselect the recommended 10ml variant (same product's 10ml)
     */
    preselectRecommended: function() {
      if (window.travelCompendium && window.travelCompendium.recommendedVariantId) {
        const grid = document.getElementById('tc-travel-sets-grid');
        const recommended = grid.querySelector(`[data-variant-id="${window.travelCompendium.recommendedVariantId}"]`);

        if (recommended) {
          const setData = window.travelCompendium.travelSets.find(
            s => String(s.variantId) === String(window.travelCompendium.recommendedVariantId)
          );
          if (setData) {
            this.selectTravelSet(recommended, setData);
            recommended.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }
    },

    /**
     * Handle 10ml fragrance selection
     */
    selectTravelSet: function(element, setData) {
      const grid = document.getElementById('tc-travel-sets-grid');

      // Remove previous selection
      grid.querySelectorAll('.tc-modal__item').forEach(item => {
        item.classList.remove('selected');
        item.setAttribute('aria-selected', 'false');
      });

      // Add selection
      element.classList.add('selected');
      element.setAttribute('aria-selected', 'true');
      this.selectedTravelSet = setData;

      // Update footer
      document.getElementById('tc-selected-name').textContent = setData.productTitle;
      var priceFreEl = document.getElementById('tc-price-free');
      if (priceFreEl) priceFreEl.textContent = 'FREE';

      // Enable add button
      const addBtn = this.modal.querySelector('[data-tc-action="add-both"]');
      if (addBtn) addBtn.disabled = false;

      this.trackEvent('tc_10ml_selected', {
        product_id: this.currentProductId,
        selected_product_id: setData.productId,
        selected_variant_id: setData.variantId,
        selected_title: setData.productTitle
      });
    },

    /**
     * Reset selection state
     */
    resetSelection: function() {
      const grid = document.getElementById('tc-travel-sets-grid');
      if (grid) {
        grid.querySelectorAll('.tc-modal__item').forEach(item => {
          item.classList.remove('selected');
          item.setAttribute('aria-selected', 'false');
        });
      }

      const selectedName = document.getElementById('tc-selected-name');
      var priceFreeEl = document.getElementById('tc-price-free');

      if (selectedName) selectedName.textContent = 'None';
      if (priceFreeEl) priceFreeEl.textContent = '';

      const addBtn = this.modal.querySelector('[data-tc-action="add-both"]');
      if (addBtn) addBtn.disabled = true;
    },

    /**
     * Handle decline - add just the 88ml
     */
    decline: function() {
      this.setDeclined(this.currentProductId);
      this.close();

      if (this.productFormElement && typeof this.productFormElement.proceedWithAddToCart === 'function') {
        this.productFormElement.proceedWithAddToCart();
      } else {
        this.addToCartDirect(this.currentVariantId, () => {
          this.openCartPopup();
        });
      }

      this.trackEvent('tc_modal_declined', {
        product_id: this.currentProductId,
        variant_id: this.currentVariantId
      });
    },

    /**
     * Add both 88ml and complimentary 10ml to cart
     * Shopify's automatic "Buy X Get Y" discount handles the free 10ml
     */
    addBothToCart: function() {
      if (!this.selectedTravelSet || this.isProcessing) return;
      this.isProcessing = true;

      this.showLoader();

      // Add both items to cart in a single request
      const items = [
        { id: Number(this.currentVariantId), quantity: 1 },
        { id: this.selectedTravelSet.variantId, quantity: 1 }
      ];

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
      .then(response => response.json())
      .then(() => {
        this.hideLoader();
        this.isProcessing = false;
        this.close();
        this.openCartPopup();

        this.trackEvent('tc_free_10ml_completed', {
          product_id: this.currentProductId,
          variant_88ml_id: this.currentVariantId,
          free_10ml_product_id: this.selectedTravelSet.productId,
          free_10ml_variant_id: this.selectedTravelSet.variantId,
          free_10ml_title: this.selectedTravelSet.productTitle
        });
      })
      .catch(error => {
        console.error('Complimentary 10ml: Error adding to cart', error);
        this.hideLoader();
        this.isProcessing = false;
      });
    },

    /**
     * Direct add to cart (bypassing interceptors)
     */
    addToCartDirect: function(variantId, callback) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }]
        })
      })
      .then(response => response.json())
      .then(() => {
        if (typeof callback === 'function') callback();
      })
      .catch(error => {
        console.error('Complimentary 10ml: Error adding to cart', error);
      });
    },

    /**
     * Open the cart popup/drawer
     */
    openCartPopup: function() {
      if (window.cart_popup && typeof window.cart_popup.updateCartPopup === 'function') {
        window.cart_popup.updateCartPopup(function() {
          if (typeof $ !== 'undefined') {
            $('.cart-popup-wrapper').addClass('open');
            $('html').addClass('cart-popup-wrapper-open');
          }
        });
      } else {
        const cartIcon = document.querySelector('.nav-bag-li a');
        if (cartIcon) cartIcon.click();
      }
    },

    showLoader: function() {
      const loader = this.modal.querySelector('.tc-modal__loader');
      if (loader) {
        loader.style.display = 'flex';
        loader.setAttribute('aria-hidden', 'false');
      }
    },

    hideLoader: function() {
      const loader = this.modal.querySelector('.tc-modal__loader');
      if (loader) {
        loader.style.display = 'none';
        loader.setAttribute('aria-hidden', 'true');
      }
    },

    formatMoney: function(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, window.money_format || window.currency_symbol);
      }
      return (cents / 100).toFixed(2);
    },

    hideGorgiasChat: function() {
      const chatButton = document.getElementById('chat-button');
      if (chatButton) chatButton.style.display = 'none';
    },

    showGorgiasChat: function() {
      const chatButton = document.getElementById('chat-button');
      if (chatButton) chatButton.style.display = '';
    },

    /**
     * Track analytics events
     */
    trackEvent: function(eventName, data) {
      if (typeof gtag === 'function') {
        gtag('event', eventName, {
          event_category: 'Complimentary 10ml',
          ...data
        });
      }

      if (typeof ga === 'function') {
        ga('send', 'event', 'Complimentary 10ml', eventName, JSON.stringify(data));
      }

      document.dispatchEvent(new CustomEvent('travelCompendium:' + eventName, {
        detail: data
      }));
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      TravelCompendium.init();
    });
  } else {
    TravelCompendium.init();
  }

  window.TravelCompendium = TravelCompendium;
})();
